<template>
  <div class="table-container">
    <div class="header">
      <div class="left">
        <div class="title">
          OKR {{ tm('title') }}
        </div>
        <RemindAction
          :user-ids="selectedRowKeys"
          placement="bottom"
        >
          <a-button
            :disabled="selectedRowKeys.length === 0"
            class="bg-btn"
          >
            {{ tm('batchRemind') }}
          </a-button>
        </RemindAction>
      </div>
      <UserStatusTableFilter
        @filter="onFilter"
      />
    </div>
    <a-table
      :data-source="data"
      :columns="columns"
      row-key="userId"
      :pagination="pagination"
      :loading="isLoading"
      :row-selection="{ selectedRowKeys, onChange: onSelectChange }"
      :scroll="{ x: 1400 }"
      @change="onChange"
    >
      <template #userDisplayName="{ record }">
        <router-link :to="{ name: 'UserOkr', params: { userId: record.userId } }">
          {{ record.userDisplayName }}
        </router-link>
      </template>

      <template #userCycleOkrDesc="{ record }">
        {{ tm('data.' + record.userCycleOkrDesc) }}
      </template>
      <template #userCyclePublishDesc="{ record }">
        {{ tm('data.' + record.userCyclePublishDesc) }}
      </template>
      <template #userCycleLinkToDesc="{ record }">
        {{ tm('data.' + record.userCycleLinkToDesc) }}
      </template>
      <template #userCycleUpdateDesc="{ record }">
        {{ tm('data.' + record.userCycleUpdateDesc) }}
      </template>
      <template #userCycleReviewDesc="{ record }">
        {{ tm('data.' + record.userCycleReviewDesc) }}
      </template>

      <template #userCycleLatestProgressUpdateTimeFormatMeasure="{ record }">
        {{ $filters.formatDate(record.userCycleLatestProgressUpdateTimeFormatMeasure, false) }}
      </template>
      <template #userCycleLatestReviewUpdateTimeFormatMeasure="{ record }">
        {{ $filters.formatDate(record.userCycleLatestReviewUpdateTimeFormatMeasure, false) }}
      </template>
      <template #userCycleLatestRemindTimeMeasure="{ record }">
        {{ $filters.formatDate(record.userCycleLatestRemindTimeMeasure) }}
      </template>
      <template #action="{ record }">
        <RemindAction
          :user-ids="[record.userId]"
          placement="bottomRight"
        >
          <a-button type="link">
            {{ tm('remind.name') }}
          </a-button>
        </RemindAction>
      </template>
    </a-table>
  </div>
</template>

<script>
import { defineComponent, ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'

import { getUserStatus } from '@/graphql/statistic'
import UserStatusTableFilter from './UserStatusTableFilter'
import RemindAction from './RemindAction'

export default defineComponent({
  name: 'UserStatusTable',
  components: {
    UserStatusTableFilter,
    RemindAction
  },
  props: {
    departmentIds: {
      type: Array,
      required: true
    }
  },
  setup (props) {
    const { state } = useStore()
    const { tm, rt } = useI18n()
    const isLoading = ref(false)
    const cycleId = computed(() => state.selectedCycleId)
    const columns = computed(() => [
      {
        title: tableTm('column.name'),
        dataIndex: 'userDisplayName',
        width: 100,
        sorter: true,
        slots: {
          customRender: 'userDisplayName'
        },
        rawName: 'User.display_name'
      },
      {
        title: tableTm('column.department'),
        dataIndex: 'departmentCompleteName',
        sorter: true,
        rawName: 'Department.complete_name'
      },
      {
        title: tableTm('column.fillingStatus'),
        dataIndex: 'userCycleOkrDesc',
        width: 100,
        sorter: true,
        align: 'center',
        slots: {
          customRender: 'userCycleOkrDesc'
        },
        rawName: 'UserCycle.okr_desc'
      },
      {
        title: tableTm('column.publishedStatus'),
        dataIndex: 'userCyclePublishDesc',
        width: 100,
        sorter: true,
        align: 'center',
        slots: {
          customRender: 'userCyclePublishDesc'
        },
        rawName: 'UserCycle.publish_desc'
      },
      {
        title: tableTm('column.alignedStatus'),
        dataIndex: 'userCycleLinkToDesc',
        width: 100,
        sorter: true,
        align: 'center',
        slots: {
          customRender: 'userCycleLinkToDesc'
        },
        rawName: 'UserCycle.link_to_desc'
      },
      {
        title: tableTm('column.alignedNum'),
        dataIndex: 'userCycleTotalLinkToCount',
        width: 100,
        sorter: true,
        align: 'center',
        rawName: 'UserCycle.total_link_to_count'
      },
      {
        title: tableTm('column.updatedStatus'),
        dataIndex: 'userCycleUpdateDesc',
        width: 120,
        sorter: true,
        align: 'center',
        slots: {
          customRender: 'userCycleUpdateDesc'
        },
        rawName: 'UserCycle.update_desc'
      },
      {
        title: tableTm('column.lastUpdatedTime'),
        dataIndex: 'userCycleLatestProgressUpdateTimeFormatMeasure',
        width: 120,
        sorter: true,
        align: 'center',
        rawName: 'UserCycle.latest_progress_update_time_format_measure:',
        slots: {
          customRender: 'userCycleLatestProgressUpdateTimeFormatMeasure'
        }
      },
      {
        title: tableTm('column.reviewedStatus'),
        dataIndex: 'userCycleReviewDesc',
        width: 100,
        sorter: true,
        align: 'center',
        slots: {
          customRender: 'userCycleReviewDesc'
        },
        rawName: 'UserCycle.review_desc'
      },
      {
        title: tableTm('column.lastReviewedTime'),
        dataIndex: 'userCycleLatestReviewUpdateTimeFormatMeasure',
        width: 120,
        sorter: true,
        align: 'center',
        rawName: 'UserCycle.latest_review_update_time_format_measure',
        slots: {
          customRender: 'userCycleLatestReviewUpdateTimeFormatMeasure'
        }
      },
      {
        title: tableTm('column.lastRemindTime'),
        dataIndex: 'userCycleLatestRemindTimeMeasure',
        width: 200,
        sorter: true,
        align: 'center',
        rawName: 'UserCycle.latest_remind_time_measure',
        slots: {
          customRender: 'userCycleLatestRemindTimeMeasure'
        }
      },
      {
        title: tableTm('column.action'),
        width: 80,
        align: 'center',
        slots: {
          customRender: 'action'
        }
      }
    ])
    const pagination = ref({
      total: 0,
      current: 1,
      pageSize: 10,
      showQuickJumper: true,
      showSizeChanger: true,
      pageSizeOptions: [
        '10',
        '20',
        '50',
        '100'
      ],
      showTotal (total) {
        return rt(tableTm('total'), { count: total })
      }
    })
    const selectedRowKeys = ref([])
    const filters = ref([])
    let orderMap

    const data = ref([])

    watch([
      cycleId,
      filters,
      () => props.departmentIds
    ], () => loadData())

    loadData()

    function loadData (offset = 0, limit = pagination.value.pageSize) {
      if (cycleId.value === null || props.departmentIds.length === 0) {
        return
      }
      isLoading.value = true
      return getUserStatus(cycleId.value, props.departmentIds, limit, offset, filters.value, orderMap)
        .then(res => {
          pagination.value.total = res.reportUserCount
          data.value = res.reportUserStat.data
        })
        .finally(() => {
          isLoading.value = false
        })
    }

    function onChange (paginationEvent, filters, sorter) {
      if (sorter.order) {
        orderMap = {
          [sorter.column.rawName]: sorter.order.slice(0, -3)
        }
      } else {
        orderMap = undefined
      }
      pagination.value.pageSize = paginationEvent.pageSize
      const offset = (paginationEvent.current - 1) * paginationEvent.pageSize
      loadData(offset).then(() => {
        pagination.value.current = paginationEvent.current
      })
    }

    function onFilter (filtersEvent) {
      filters.value = filtersEvent
    }

    function onSelectChange (selectRowKeysEvent) {
      selectedRowKeys.value = selectRowKeysEvent
    }

    function tableTm (name) {
      return tm('statistic.table.' + name)
    }

    return {
      isLoading,
      columns,
      pagination,
      selectedRowKeys,
      data,
      onChange,
      onFilter,
      onSelectChange,
      tm: tableTm
    }
  }
})
</script>

<style lang="less" scoped>
.table-container {
  padding: 16px 0;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 0 6px 0 rgba(39, 52, 125, 0.08);
  margin-bottom: 16px;

  .header {
    padding: 0 12px;
    margin-bottom: 12px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .title {
    font-size: 16px;
    font-weight: bold;
    margin-right: 8px;
  }
}

:deep(.ant-table-thead) {
  & > tr > th {
    padding: 12px 8px;
  }
}

:deep(.ant-table-tbody) {
  & > tr > td {
    padding: 12px 8px;
  }
}

:deep(.ant-table-pagination.ant-pagination) {
  margin: 16px 8px 16px 0;
}

.left {
  display: flex;
  align-items: center;
}
</style>
